/* eslint-disable consistent-return */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import { isPortalWithEcommerce, getIsTransitionOrBundleCredit } from '@panel/commons/helpers';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useCopyToClipboard } from '@panel/commons/hooks/useCopyToClipboard';
import CopyIcon from '@panel/commons/assets/icons/copy-gray.svg';
import TrendOrange from '@panel/commons/assets/icons/trending-up-orange.svg';
import TrendCM from '@panel/commons/assets/icons/trending-up-cm.svg';
import TrendVA from '@panel/commons/assets/icons/trending-up-va.svg';
import TrendUB from '@panel/commons/assets/icons/trending-up-ub.svg';
import TrendDark from '@panel/commons/assets/icons/trending-up-darkgrey.svg';
import TrendLight from '@panel/commons/assets/icons/trending-up-light.svg';
import { sendTrackingInfo } from '@panel/commons/';

const siteId = process.env.REACT_APP_BUILD_SITE;

const BottomInfoRight = ({ posting, device, isDraft, isParticular, openModal, postingStatus, isAssistant }) => {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [value, copy] = useCopyToClipboard();
  const [copyToClipboardMessage, setCopyToClipboardMessage] = useState({ id: '', codigo: '' });
  const [copyTimeOutRef, setCopyTimeOutRef] = useState('');

  const { postingId, publicationPlan, internalCode, hasLinkUpgradePlan, publicationPlanId, publicationName } = posting;
  const isTransitionOrBundleCredit = getIsTransitionOrBundleCredit(publicationPlanId);
  const hasUpgrade = !isDraft && publicationPlan;

  const beginDate = posting.stateAndDates[0] && posting.stateAndDates[0].beginDate;
  const status = posting.stateAndDates[0] && posting.stateAndDates[0].status;
  const endDate = posting.stateAndDates[0] && posting.stateAndDates[0].endDate;
  const createDate = posting.stateAndDates[0] && posting.stateAndDates[0].createDate;
  const finishedDate = status === 'OFFLINE' && posting.stateAndDates[0] && posting.stateAndDates[0].endDate;
  const deletedDate = status === 'DELETED' && posting.stateAndDates[0] && posting.stateAndDates[0].finishedDate;
  const withoutCredit = status === 'WITHOUT_CREDIT';
  const isOffline = ['OFFLINE', 'WITHOUT_CREDIT', 'DELETED'].includes(status);
  const isDevelopment = posting.publicationType && posting.publicationType !== 'Clasificado';
  const enableUpgradeButton = hasLinkUpgradePlan && !isDevelopment && !isOffline;

  // esta variable se crea ya que los usuarios particulares en LLVE no tienen disponible el ecommerce
  const isPortalWithoutEcommerce = isParticular && !isPortalWithEcommerce;

  const linkUpgradePlan = `../panel/publicador/planes/seleccion?postingId=${postingId}`;

  const showCopyMessage = () => {
    clearTimeout(copyTimeOutRef);
    setCopyTimeOutRef(
      setTimeout(() => {
        setCopyToClipboardMessage({ id: '', codigo: '' });
      }, 2000)
    );
  };

  useEffect(() => {
    if (copyToClipboardMessage.id || copyToClipboardMessage.codigo) {
      showCopyMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyToClipboardMessage.id, copyToClipboardMessage.codigo]);

  return (
    <Container>
      {internalCode && (
        <BottomInfo>
          <div className='info-label'>Código</div>
          <div className='info-text with-copy'>
            <div data-panel='posting-codigo'>{internalCode}</div>
            <SimpleTooltip
              isMobile={device === 'tablet'}
              offset={10}
              delay={copyToClipboardMessage.codigo ? 0 : 300}
              delayOut={copyToClipboardMessage.codigo ? 1500 : 200}
              position='top-center'
              text={copyToClipboardMessage.codigo ? copyToClipboardMessage.codigo : `${t('panel.posting.copy.codigo')}`}
              dataPanel='info-copyCodigo'
            >
              <CopyButton
                onClick={() =>
                  copy(
                    internalCode,
                    setCopyToClipboardMessage({
                      ...copyToClipboardMessage,
                      codigo: t('panel.posting.copy.codigo.success')
                    })
                  )
                }
                data-panel='posting-copyCodigo'
              />
            </SimpleTooltip>
          </div>
        </BottomInfo>
      )}
      {postingId && (
        <BottomInfo>
          <div className='info-label'>ID</div>
          <div className='info-text with-copy'>
            <div data-panel='posting-id'>{postingId}</div>
            <SimpleTooltip
              isMobile={device === 'tablet'}
              offset={10}
              delay={copyToClipboardMessage.id ? 0 : 300}
              delayOut={copyToClipboardMessage.id ? 1500 : 200}
              position='top-center'
              text={copyToClipboardMessage.id ? copyToClipboardMessage.id : `${t('panel.posting.copy.codigo')}`}
              dataPanel='info-copyId'
            >
              <CopyButton
                onClick={() =>
                  copy(
                    postingId,
                    setCopyToClipboardMessage({
                      ...copyToClipboardMessage,
                      id: t('panel.posting.copy.id.success')
                    })
                  )
                }
                data-panel='posting-copyId'
              />
            </SimpleTooltip>
          </div>
        </BottomInfo>
      )}
      {device !== 'tablet' && (
        <>
          {beginDate && !finishedDate && (
            <BottomInfo>
              <div className='info-label'>{t('postings.card.status.ONLINE.beginDate')}</div>
              <div className='info-date' data-panel='posting-beginDate'>
                {beginDate}
              </div>
            </BottomInfo>
          )}
          {createDate && isDraft && (
            <BottomInfo>
              <div className='info-label'>{t('postings.card.status.DRAFT.createDate')}</div>
              <div className='info-date' data-panel='posting-createDate'>
                {createDate}
              </div>
            </BottomInfo>
          )}
          {endDate && !finishedDate && (
            <BottomInfo>
              <div className='info-label'>
                {t(`postings.card.status.${!withoutCredit ? postingStatus : 'ONLINE'}.endDate`)}
              </div>
              <div className='info-date' data-panel='posting-endDate'>
                {endDate}
              </div>
            </BottomInfo>
          )}
          {finishedDate && (
            <BottomInfo>
              <div className='info-label'>{t('postings.card.status.OFFLINE.endDate')}</div>
              <div className='info-date' data-panel='posting-endDateFinished'>
                {finishedDate}
              </div>
            </BottomInfo>
          )}
          {deletedDate && (
            <BottomInfo>
              <div className='info-label'>{t('postings.card.status.DELETED.endDate')}</div>
              <div className='info-date' data-panel='posting-deletedDate'>
                {deletedDate}
              </div>
            </BottomInfo>
          )}
        </>
      )}
      {publicationPlan && (
        <BottomInfo>
          <div className='info-label'>{t('panel.posting.plan')}</div>
          <div data-panel='posting-plan' className='info-text overflow-handler'>
            {isTransitionOrBundleCredit ? publicationName : t(`panel.avisos.planName.${publicationPlan}`)}
          </div>
        </BottomInfo>
      )}
      {hasUpgrade && !isPortalWithoutEcommerce && !isAssistant && (
        <PlanUpgradeLink
          enableUpgradeButton={enableUpgradeButton}
          data-panel='posting-upgradeLink'
          href={isParticular ? linkUpgradePlan : null}
          rel='noopener noreferrer'
          onClick={e => {
            sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Mejora tu plan');
            if (!isParticular) {
              return enableUpgradeButton && openModal(e, 'upgradePlanModal');
            }
          }}
          device={device}
        >
          <span>{t('panel.avisos.posting.CTA.improveExposition')}</span>
          <div className='button-icon' />
        </PlanUpgradeLink>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
`;

const BottomInfo = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
  padding: 0 9.5px 0 8px;
  height: 27px;
  border-right: 1px solid ${select('grayMedium')};
  color: ${select('darkGray')};
  &:last-of-type {
    border: none;
  }
  &:first-of-type {
    padding-left: 0;
  }
  :last-of-type {
    padding-right: 0;
  }
  .info-label {
    margin-right: 4px;
  }
  .info-date {
    font-weight: 500;
  }
  .info-text {
    font-weight: 500;
    color: black;
    &.overflow-handler {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      width: 180px;
    }
  }
  .with-copy {
    position: relative;
    display: flex;
    width: 60px;
    > div:first-of-type {
      max-width: 60px;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    span {
      display: none;
    }
    :hover {
      span {
        display: flex;
      }
      > div:first-of-type {
        max-width: 50px;
      }
    }
  }
`;

const CopyButton = styled.span`
  display: inline-block;
  content: '';
  background-image: ${`url(${CopyIcon})`};
  background-repeat: no-repeat;
  background-size: cover;
  width: 20px;
  height: 20px;
  left: 0;
  cursor: pointer;
`;

const PlanUpgradeLink = styled.a`
  font-family: Hind;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  display: flex;
  border-radius: 5px;
  padding: 4px 12px;
  margin-left: 20px;
  background-color: ${select('lightGray')};
  color: ${select('midLightGray')};
  .button-icon {
    background-image: ${`url(${TrendLight})`};
    margin-left: 5px;
    height: 16px;
    background-size: cover;
    width: 16px;
    background-repeat: no-repeat;
  }
  ${({ enableUpgradeButton }) =>
    enableUpgradeButton &&
    css`
      cursor: pointer;
      color: black;
      box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.18);
      .button-icon {
        background-image: ${`url(${TrendDark})`};
      }
      :hover {
        color: ${getColor('primary')};
        .button-icon {
          background-image: ${() => {
            if (siteId === 'VAMX') return `url(${TrendVA})`;
            if (siteId === 'CMBR') return `url(${TrendCM})`;
            if (siteId === 'UBPE') return `url(${TrendUB})`;
            return `url(${TrendOrange})`;
          }};
        }
      }
    `}
`;

export default BottomInfoRight;
